import React from 'react';
import { ArrowRightIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { useFeatureFlagEnabled } from 'posthog-js/react';

interface NextActionBoxProps {
  /**
   * The user's selected journey variant from signup (e.g., 'cicd', 'dbt')
   * If provided, this journey will be highlighted as the primary action
   */
  userJourneyVariant?: string | null;
}

/**
 * Journey configuration type
 */
interface Journey {
  variant: string;
  name: string;
  description: string;
  url: string;
}

/** PostHog feature flag name that controls the automated naming for create URL paths */
const RENAME_DYNAMIC_TO_AUTOMATED_FLAG = 'mtm-rename-dynamic-to-automated';

/**
 * Build environment-specific journey configurations.
 *
 * @param useAutomatedNaming - When true, use new automated-* path names;
 *   when false, use the legacy dynamic-* path names.
 */
function buildJourneyConfigs(
  useAutomatedNaming: boolean,
): Record<string, { journeys: Journey[]; exploreAllUrl: string }> {
  const cicdPath = useAutomatedNaming ? 'data-products/create/automated-cicd' : 'data-products/create/dynamic-delivery';
  const migrationPath = useAutomatedNaming
    ? 'data-products/create/automated-dbt-migration'
    : 'data-products/create/dynamic-transformation';

  return {
    production: {
      journeys: [
        {
          variant: 'cicd',
          name: 'Automated CI/CD',
          description: 'CI/CD for DataOps',
          url: `https://momentum.dataops.live/${cicdPath}`,
        },
        {
          variant: 'dbt',
          name: 'Automated DBT Migration',
          description: 'dbt transformation pipeline',
          url: `https://momentum.dataops.live/${migrationPath}`,
        },
      ],
      exploreAllUrl: 'https://momentum.dataops.live',
    },
    az: {
      journeys: [
        {
          variant: 'cicd',
          name: 'Automated CI/CD',
          description: 'CI/CD for DataOps',
          url: `https://momentum.az.dataops.live/${cicdPath}`,
        },
        {
          variant: 'dbt',
          name: 'Automated DBT Migration',
          description: 'dbt transformation pipeline',
          url: `https://momentum.az.dataops.live/${migrationPath}`,
        },
      ],
      exploreAllUrl: 'https://momentum.az.dataops.live',
    },
    qa: {
      journeys: [
        {
          variant: 'cicd',
          name: 'Automated CI/CD',
          description: 'CI/CD for DataOps',
          url: `https://momentum.qa.dataops.live/${cicdPath}`,
        },
        {
          variant: 'dbt',
          name: 'Automated DBT Migration',
          description: 'dbt transformation pipeline',
          url: `https://momentum.qa.dataops.live/${migrationPath}`,
        },
      ],
      exploreAllUrl: 'https://momentum.qa.dataops.live',
    },
    dev: {
      journeys: [
        {
          variant: 'cicd',
          name: 'Automated CI/CD',
          description: 'CI/CD for DataOps',
          url: `https://momentum.qa.dataops.live/${cicdPath}`,
        },
        {
          variant: 'dbt',
          name: 'Automated DBT Migration',
          description: 'dbt transformation pipeline',
          url: `https://momentum.qa.dataops.live/${migrationPath}`,
        },
      ],
      exploreAllUrl: 'https://momentum.qa.dataops.live',
    },
  };
}

/**
 * NextActionBox Component
 *
 * Displays create flow links with the user's selected journey highlighted
 * as the primary next action. Shows all available create flows based on
 * the current environment.
 *
 * Each environment has its own explicit journey configuration, allowing for
 * different journey options, titles, descriptions, or URLs per environment.
 *
 * @component
 * @example
 * ```tsx
 * // With user journey
 * <NextActionBox userJourneyVariant="cicd" />
 *
 * // Without user journey (all equal weight)
 * <NextActionBox />
 * ```
 */
const NextActionBox: React.FC<NextActionBoxProps> = ({ userJourneyVariant }) => {
  // Feature flag: when enabled, use automated-* path names instead of dynamic-* names
  const useAutomatedNaming = useFeatureFlagEnabled(RENAME_DYNAMIC_TO_AUTOMATED_FLAG) === true;

  // Get environment-specific configuration
  const env = process.env.REACT_APP_ENV ?? 'dev';
  const JOURNEY_CONFIGS = buildJourneyConfigs(useAutomatedNaming);
  const config = JOURNEY_CONFIGS[env] ?? JOURNEY_CONFIGS.dev;
  const { journeys, exploreAllUrl } = config;

  // Find the user's selected journey if one exists
  const primaryJourney = journeys.find((j) => j.variant === userJourneyVariant);

  return (
    <div className="bg-gradient-to-r from-dataops-primary-blue to-dataops-amethyst-purple rounded-lg shadow-lg p-6 text-white">
      <div className="flex items-center gap-2 mb-4">
        <RocketLaunchIcon className="h-6 w-6" aria-hidden="true" />
        <h2 className="text-xl text-white">Next Action</h2>
      </div>

      {/* Primary action if user has a selected journey */}
      {primaryJourney != null && (
        <div className="mb-6">
          <p className="text-sm text-white/90 mb-3">Continue your journey:</p>
          <a
            href={primaryJourney.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 border border-transparent text-lg rounded-md text-dataops-secondary-blue bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-dataops-primary-blue transition-all transform hover:scale-105 shadow-md"
            aria-label={`Start ${primaryJourney.name}`}
          >
            <span className="font-medium">{primaryJourney.name}</span>
            <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
          </a>
          <p className="text-xs text-white/80 mt-2">{primaryJourney.description}</p>
        </div>
      )}

      {/* Other create flows */}
      <div className={primaryJourney != null ? 'pt-4 border-t border-white/20' : ''}>
        <p className="text-sm text-white/90 mb-3">
          {primaryJourney != null ? 'Or explore other options:' : 'Get started with:'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {journeys
            .filter((j) => j.variant !== userJourneyVariant)
            .map((journey) => (
              <a
                key={journey.variant}
                href={journey.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 bg-black/10 hover:bg-black/20 rounded-md transition-colors backdrop-blur-sm border border-black/20"
                aria-label={`Start ${journey.name}`}
              >
                <div>
                  <p className="text-sm text-white">{journey.name}</p>
                  <p className="text-xs text-white/70">{journey.description}</p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-white/70 flex-shrink-0 ml-2" aria-hidden="true" />
              </a>
            ))}

          {/* Generic explore all link */}
          <a
            href={exploreAllUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-4 py-3 bg-black/10 hover:bg-black/20 rounded-md transition-colors backdrop-blur-sm border border-black/20"
            aria-label="Explore all create options"
          >
            <div>
              <p className="text-sm text-white">Explore All Options</p>
              <p className="text-xs text-white/70">Browse all create flows</p>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-white/70 flex-shrink-0 ml-2" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default NextActionBox;
